<template>
  <div class="shadow-popover-light flex h-10 items-center rounded-lg bg-white p-1">
    <fw-popover
      :position="'top-center'"
      :offset-x="popoverOffsetX"
      :offset-y="-26"
      :close-on-click-inside-selector="'button'"
      popover-container-class="!shadow-lg !border-0 bg-white"
    >
      <template #trigger>
        <fw-button :textButton="true" size="medium" :disabled="!isEditorLoaded" :class="'hover:bg-transparent-dark-2 !px-2'">
          <fw-icon class="mr-1" name="download-2" :width="20" :height="20" />
          <span class="download-text text-sm">{{ $T('Download') }}</span>
        </fw-button>
      </template>
      <template #content>
        <div class="font-NeverMind flex flex-col gap-1 rounded-lg px-3 py-4 ring-1 ring-black ring-opacity-5">
          <button
            v-for="item of downloadItems"
            :key="item.id"
            class="text-desktop-body-1 hover:bg-transparent-dark-2 flex w-[120px] items-center gap-3 rounded-md px-3 py-2 transition-colors"
            :disabled="!isEditorLoaded"
            @click="item.onClick"
          >
            <img :src="item.icon" :alt="item.title" class="h-5 w-5" />
            <span>{{ item.title }}</span>
          </button>
        </div>
      </template>
    </fw-popover>

    <div class="bg-grey-300 ml-1 mr-2 h-5 w-px"></div>

    <fw-button size="medium" theme="secondary" :disabled="!isEditorLoaded" @click="handleSaveOnline">
      <span class="text-sm">{{ $T('Save Online') }}</span>
    </fw-button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { $T } from 'client/library/translation'
import { useWorkspaceStore } from 'app/services/pinia/workspace-store'
import { exportPngAction, exportPDFAction } from 'client/pages/integration-embed-editor/action/export-action'
import { useEmbedEvents } from 'client/pages/integration-embed-editor/composables/use-embed-events'
import { useEditorHelper } from 'client/pages/integration-embed-editor/patterns/composables/use-editor-helper'

import IconXmind from 'import-files/images/tools/markdown-to-mind-map/icon-xmind.svg?url'
import IconPdf from 'import-files/images/tools/markdown-to-mind-map/icon-pdf.svg?url'
import IconPng from 'import-files/images/tools/markdown-to-mind-map/icon-img.svg?url'
import { useTracking } from 'client/composables/use-tracking'
import { useWindowSize } from '@vueuse/core'

const isEditorLoaded = computed(() => useWorkspaceStore().editor?.mapStatus === 'loaded')
const workbook = computed(() => useWorkspaceStore().workbook)
const { trackClick, trackActionCompleted } = useTracking({ mixpanel: true })
const { width: windowWidth } = useWindowSize()
const popoverOffsetX = computed(() => (windowWidth.value <= 343 ? 50 : 14))

const handleDownloadXmind = async () => {
  if (!isEditorLoaded.value || !workbook.value) return
  trackClick('download_file', { format: 'xmind' })
  const buffer = await useEditorHelper().downloadFile(workbook.value.document.value, workbook.value?.stage)
  useEmbedEvents().emit('download-xmind-file', buffer)
  trackActionCompleted({ action: 'file_downloaded' })
}

const handleExportPng = async () => {
  if (!isEditorLoaded.value) return
  trackClick('download_file', { format: 'png' })
  const blob = await exportPngAction()
  useEmbedEvents().emit('export-png', blob)
  trackActionCompleted({ action: 'file_downloaded' })
}

const handleExportPdf = async () => {
  if (!isEditorLoaded.value) return
  trackClick('download_file', { format: 'pdf' })
  const blob = await exportPDFAction()
  useEmbedEvents().emit('export-pdf', blob)
  trackActionCompleted({ action: 'file_downloaded' })
}

const downloadItems = computed(() => [
  {
    icon: IconXmind,
    id: 'xmind',
    title: $T('Xmind'),
    onClick: handleDownloadXmind,
  },
  {
    icon: IconPdf,
    id: 'pdf',
    title: $T('PDF'),
    onClick: handleExportPdf,
  },
  {
    icon: IconPng,
    id: 'png',
    title: $T('PNG'),
    onClick: handleExportPng,
  },
])

const handleSaveOnline = async () => {
  if (!isEditorLoaded.value || !workbook.value) return
  useTracking().trackClick('save_online')
  const buffer = await useEditorHelper().downloadFile(workbook.value.document.value, workbook.value?.stage)
  useEmbedEvents().emit('save-online', buffer)
}
</script>

<style scoped>
.download-text {
  @media (max-width: 343px) {
    display: none;
  }
}
</style>
